import { motion } from "framer-motion";
import { AiOutlineLeft, AiOutlineRight, AiOutlineClose } from "react-icons/ai";
import React, { FC, useEffect, useState } from "react";
import { getAssetPathAndSize, getSrcSet } from "utils";
import styles from "../ImageGalleryMain.module.css";
import classnames from "classnames";
import classNames from "classnames";


type ImageGalleryMainItemProps = {
  Alt: string;
  Src?: string;
  backgroundItem?: any;
};

type TImageGalleryMainProps = {
  large?: boolean;
  small?: boolean;
  images: ImageGalleryMainItemProps[];
  className?: string;
  width?: string;
  height?: string;
  ShowCaptions: boolean;
};

const _ImageGalleryMainDesktop: FC<TImageGalleryMainProps> = ({
  large,
  small,
  images,
  ShowCaptions
}) => {
  
  const [imagePostitions, setImagePositions] = useState<number[]>(
    images.map((img, i) => i * 100)
  );
  const [fullscreen, setFullScreen] = useState<boolean>(false);
  const [fullScreenIdx, setFullScreenIdx] = useState<number>(0);
  const [ready, setReady] = useState<boolean>(true);

  // Disable document scroll when fullscreen carousel is active.
  useEffect(() => {
    if (fullscreen) {
      document.querySelector('html').classList.add('no-scroll')
    } else {
      document.querySelector('html').classList.remove('no-scroll')
    }
  }, [fullscreen]);

  const next = () => {
    const idx = activeIdx + 1;
    const loop = idx > imagePostitions.length - 1 ? true : false;
    !loop && setImagePositions(imagePostitions.map((int) => int - 100));
    loop && setImagePositions(images.map((img, i) => i * 100));
  };

  const prev = () => {
    const idx = activeIdx - 1;
    const loop = idx < 0;

    const defaultPositions = images.map((img, i) => i * 100);
    const subWith = defaultPositions[defaultPositions.length - 1];

    !loop && setImagePositions(imagePostitions.map((int) => int + 100));
    loop && setImagePositions(defaultPositions.map((p, i) => defaultPositions[i] - subWith));
  };


  const handleFullScreenToggle = (i: number) => {
    setFullScreenIdx(i);
    setFullScreen(true);
    document.getElementById("main-navigation")?.classList.remove("active");
    document.querySelector('body').classList.add('no-scroll')
  };

  const handleExitFullscreen = () => {
    setFullScreen(false);
    document.querySelector('body').classList.remove('no-scroll')
  };

  const activeItemInfo = () => {
    const i = imagePostitions.indexOf(0);
    return {
      activeIdx: i,
      Alt: images[i].Alt,
    };
  };

  const { activeIdx, Alt } = activeItemInfo();

  return (
    <section style={{ opacity: ready ? 1 : 0 }}>
      <div
        className={classnames(styles.wrapper, { large: large, small: small }, 'ImageGalleryMain')}
      >
        <div className={styles.slidesContainer}>
          {images.map((img, i) => (
            <motion.div
              key={`${i}-image`}
              animate={{
                originX: 0,
                x: `${imagePostitions[i]}%`,
                transition: {
                  type: "tween",
                  duration: 0.5,
                },
              }}
              className={styles.slideImage}
            >
              <img
                onClick={() => handleFullScreenToggle(i)}
                alt={img.Alt}
                src={getAssetPathAndSize(img.Src, 100)}
              />
            </motion.div>
          ))}
        </div>

        {!ShowCaptions && images.length === 1 ? <></> :
          <div className={styles.controlBar}>

            {images.length > 1 &&
              <div className={styles.skipWrapper}>
                <button
                  onClick={() => prev()}
                  className={styles.prevBtn}
                >
                  <AiOutlineLeft />
                </button>
                <span className="counterFeedback">
                  {activeIdx + 1} of {imagePostitions.length}
                </span>
                <button
                  onClick={() => next()}
                  className={styles.nextBtn}
                >
                  <AiOutlineRight />
                </button>
              </div>
            }

            {ShowCaptions && <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={classNames(styles.legend, { noMargin: images.length === 1 })}
              dangerouslySetInnerHTML={{ __html: Alt }}
            />}

          </div>
        }

      </div>

      <motion.div
        animate={{
          y: fullscreen ? "0" : "-100vh",
          opacity: fullscreen ? 1 : 0,
          transition: {
            type: "tween",
          },
        }}
        className={styles.fullscreenView}
      >
        <button onClick={() => handleExitFullscreen()} className={styles.exit}>
          <AiOutlineClose />
        </button>
        <button
          onClick={() =>
            fullScreenIdx > 0 ? setFullScreenIdx(fullScreenIdx - 1) : setFullScreenIdx(imagePostitions.length - 1)
          }
          className="prevButton"
        >
          <AiOutlineLeft />
        </button>
        <button
          onClick={() =>
            fullScreenIdx < imagePostitions.length - 1 ?
              setFullScreenIdx(fullScreenIdx + 1) : setFullScreenIdx(0)
          }
          className="nextButton"
        >
          <AiOutlineRight />
        </button>
        <div className="imgWrapper">
          <motion.img
            initial={{ translateY: '-150vh' }}
            animate={{ translateY: fullscreen ? '0vh' : '-150vh', transition: { delay: .5, type: "tween" } }}
            src={getAssetPathAndSize(images[fullScreenIdx].Src, 100)}
            alt={images[fullScreenIdx].Alt}
          />
        </div>
        {ShowCaptions && <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fullScreenAlt"
        >
          {images[fullScreenIdx].Alt}
        </motion.span>}

      </motion.div>
    </section>
  );
};

export const ImageGalleryMainDesktop = React.memo(_ImageGalleryMainDesktop);
